import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Eye, GraduationCap, MonitorSmartphone, Plus, Trash2, Users } from 'lucide-react';
import { Course } from '@/types';
import { courseApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useAuthStore } from '@/store/authStore';
import { PriceDisplay } from '@/components/public/PriceDisplay';

export const AdminCoursesPage = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';
  const basePath = isAdmin ? '/admin/courses' : '/tutor/courses';
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const fetchCourses = async () => {
      try {
        const data = isAdmin
          ? await courseApi.getAllCourses(controller.signal)
          : await courseApi.getMyCourses(controller.signal);
        setCourses(data);
      } catch (fetchError) {
        if ((fetchError as Error).name !== 'AbortError') {
          setError('Courses are not available right now. Please try again soon.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchCourses();
    return () => controller.abort();
  }, [isAdmin]);

  const sortedCourses = useMemo(
    () =>
      [...courses].sort((left, right) => {
        const leftOwned = left.createdById === user?.id ? 0 : 1;
        const rightOwned = right.createdById === user?.id ? 0 : 1;
        return leftOwned - rightOwned;
      }),
    [courses, user?.id]
  );

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this course?')) {
      return;
    }

    try {
      await courseApi.deleteCourse(id);
      setCourses((prev) => prev.filter((course) => course.id !== id));
    } catch (deleteError) {
      console.error('Failed to delete course:', deleteError);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="page-heading">{isAdmin ? 'Course Management' : 'My Courses'}</h1>
          <p className="page-subtitle">
            {isAdmin
              ? 'Manage every course with cleaner public metadata and a stronger curriculum structure.'
              : 'Create polished course pages and update the courses owned by your tutor account.'}
          </p>
        </div>
        <Button variant="primary" onClick={() => navigate(`${basePath}/new`)}>
          <Plus className="h-4 w-4" />
          Add Course
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="h-32 animate-pulse rounded-[1.75rem] bg-white/70" />
          ))}
        </div>
      ) : error ? (
        <Card className="rounded-[1.75rem] p-12 text-center">
          <p className="text-slate-600">{error}</p>
        </Card>
      ) : sortedCourses.length === 0 ? (
        <Card className="rounded-[1.75rem] p-12 text-center">
          <p className="text-slate-600">{isAdmin ? 'No courses found.' : 'You have not created any courses yet.'}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedCourses.map((course) => {
            const totalSubtopics = course.sections.reduce((count, section) => count + section.lessons.length, 0);
            const isOwnedByCurrentUser = course.createdById === user?.id;

            return (
              <Card key={course.id} className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
                <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                  <div className="flex flex-1 flex-col gap-4 sm:flex-row">
                    <img src={course.thumbnailUrl} alt={course.title} className="h-40 w-full rounded-[1.25rem] object-cover sm:h-24 sm:w-36" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-xl font-semibold text-slate-950">{course.title}</h2>
                        {!isAdmin ? (
                          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                            Your course
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">{course.description}</p>
                      <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                        <p className="flex items-center gap-2">
                          <GraduationCap className="h-4 w-4" />
                          {course.level}
                        </p>
                        <p className="flex items-center gap-2">
                          <MonitorSmartphone className="h-4 w-4" />
                          {course.teachingMode || 'Online'}
                        </p>
                        <p className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {course.audience?.length ? course.audience.join(', ') : 'Open to all learners'}
                        </p>
                        <p>{course.sections.length} topics and {totalSubtopics} subtopics</p>
                        {course.tutor?.name ? <p>Tutor: {course.tutor.name}</p> : null}
                        {isAdmin && course.createdByName ? <p>Owner: {course.createdByName}</p> : null}
                        {course.price != null || course.isFree ? (
                          <PriceDisplay
                            isFree={course.isFree}
                            originalPrice={course.price != null && course.price > 0 ? `INR ${course.price.toLocaleString('en-IN')}` : undefined}
                          />
                        ) : null}
                        {!isAdmin && !isOwnedByCurrentUser ? <p className="text-amber-700">Read-only</p> : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 xl:ml-4">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/courses/${course.slug}`)}>
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => navigate(`${basePath}/${course.id}/edit`)}>
                      <Edit className="h-4 w-4" />
                      Edit
                    </Button>
                    {isAdmin ? (
                      <Button variant="danger" size="sm" onClick={() => handleDelete(course.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
